import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createQueryKeys } from '@lukemorales/query-key-factory'
import type {
  AddRundownColumnParams,
  AddRundownPrivateNotesColumnParams,
  ClearRundownColumnParams,
  DeleteRundownColumnParams,
  GetRundownColumnsParams,
  GetRundownParams,
  GroupRundownCuesParams,
  ReorderRundownCuesParams,
  UploadRundownFileParams,
  AddRundownCueParams,
  UpdateRundownCueParams,
  DuplicateRundownCueParams,
  DeleteRundownCueParams,
  UpdateRundownParams,
  CreateRundownRunnerParams,
  UpdateRundownColumnParams,
  ReorderRundownColumnsParams,
  DuplicateRundownCuesParams,
  UngroupRundownCuesParams,
  ReorderRundownPrivateColumnParams,
  GetRundownsByTeamParams,
  CloneRundownParams,
} from '../model/params.ts'
import {
  addRundownColumn,
  addRundownPrivateColumn,
  clearRundownColumn,
  clearRundownPrivateColumn,
  getRundown,
  groupRundownCues,
  reorderRundownCues,
  uploadRundownFile,
  addRundownCue,
  updateRundownCue,
  duplicateRundownCue,
  deleteRundownCue,
  updateRundown,
  createRundownRunner,
  updateRundownColumn,
  reorderRundownColumns,
  duplicateRundownCues,
  getCellHistory,
  updateRundownCell,
  ungroupRundownCues,
  reorderRundownPrivateColumn,
  deleteRundownColumn,
  getRundownsByTeam,
  getTemplatesByTeam,
  cloneRundown,
} from './requests.ts'
import type { MutationHook, QueryHook } from '@/lib/react-query'
import type {
  AddRundownColumnDto,
  UploadRundownFileDto,
  AddRundownCueDto,
  DuplicateRundownCueDto,
  DuplicateRundownCuesDto,
} from '../model/dto.ts'
import type {
  Rundown,
  RundownCell,
  RundownCue,
  Runner,
  Column,
  Cell,
} from '@rundown-studio/types'
import { useSetAtom } from 'jotai'
import {
  setCueOrderAtom,
  setCuesAtom,
  setCellsAtom,
  rundownAtom,
  setCueAtom,
} from '../../../store/rundown.store'
import { toastAtom } from '../../../store/toast.store'

export const rundownQueryKeys = createQueryKeys('RUNDOWN', {
  byId: (params: GetRundownParams) => [params],

  byTeamId: (params: GetRundownsByTeamParams) => [params],

  templatesByTeamId: (params: GetRundownsByTeamParams) => [params],

  columns: (params: GetRundownColumnsParams) => [params],

  cellHistory: (rundownId: string, cellId: string) => [{ rundownId, cellId }],
})
export const RUNDOWN_MUTATION_KEYS_PREFIX = 'RUNDOWN'
export const rundownMutationKeys = {
  clearRundownPrivateColumn: [
    RUNDOWN_MUTATION_KEYS_PREFIX,
    'clearRundownPrivateColumn',
  ],
  addCue: [RUNDOWN_MUTATION_KEYS_PREFIX, 'addCue'],
}

export const useRundown = ({
  params,
  options,
}: QueryHook<Rundown, GetRundownParams>) => {
  return useQuery({
    ...options,
    queryKey: rundownQueryKeys.byId(params).queryKey,
    queryFn: async () => {
      const { data } = await getRundown(params)

      return data
    },
  })
}

export const useRundownsByTeam = ({
  params,
  options,
  hideToast,
}: QueryHook<Rundown[], GetRundownsByTeamParams>) => {
  return useQuery({
    ...options,
    queryKey: rundownQueryKeys.byTeamId(params).queryKey,
    queryFn: async () => {
      const { data } = await getRundownsByTeam(params, { toast: !hideToast })

      return data
    },
  })
}

export const useUpdateRundown = ({
  options,
}: MutationHook<Rundown, UpdateRundownParams> = {}) => {
  const setRundown = useSetAtom(rundownAtom)

  return useMutation({
    ...options,
    mutationFn: async (params) => {
      const { data } = await updateRundown(params)

      return data
    },
    onSuccess(rundown) {
      setRundown(rundown)
    },
  })
}

export const useAddRundownPrivateNotesColumn = ({
  options,
}: MutationHook<AddRundownColumnDto, AddRundownPrivateNotesColumnParams>) => {
  return useMutation({
    ...options,
    mutationFn: async (params) => {
      const { data } = await addRundownPrivateColumn({
        rundownId: params.rundownId,
        payload: {
          name: 'Private notes',
          // TODO: Think of introducing the new type for custom column types
          // columnType: 'PRIVATE_NOTES',
          type: 'richtext',
          privateUid: params.privateUid,
        },
      })

      return data
    },
    onSuccess() {
      // TODO: Add required invalidations if needed
    },
  })
}

export const useAddRundownColumn = ({
  options,
}: MutationHook<AddRundownColumnDto, AddRundownColumnParams>) => {
  const queryClient = useQueryClient()

  return useMutation({
    ...options,
    mutationFn: async (params) => {
      const { data } = await addRundownColumn(params)

      return data
    },
    onSuccess([rundown]) {
      queryClient.setQueryData(
        rundownQueryKeys.byId({
          rundownId: rundown.id,
        }).queryKey,
        rundown,
      )
    },
  })
}

export const useUpdateRundownColumn = ({
  options,
}: MutationHook<Column, UpdateRundownColumnParams> = {}) => {
  return useMutation({
    ...options,
    mutationFn: async (params) => {
      const { data } = await updateRundownColumn(params)

      return data
    },
  })
}

export const useReorderRundownColumns = ({
  options,
}: MutationHook<Column, ReorderRundownColumnsParams> = {}) => {
  return useMutation({
    ...options,
    mutationFn: async (params) => {
      const { data } = await reorderRundownColumns(params)

      return data
    },
  })
}

export const useReorderRundownPrivateColumn = ({
  options,
}: MutationHook<Rundown, ReorderRundownPrivateColumnParams> = {}) => {
  const setRundown = useSetAtom(rundownAtom)

  return useMutation({
    ...options,
    mutationFn: async (params) => {
      const { data } = await reorderRundownPrivateColumn(params)

      return data
    },
    onSuccess(rundown) {
      setRundown(rundown)
    },
  })
}

export const useDeleteRundownColumn = ({
  options,
}: MutationHook<unknown, DeleteRundownColumnParams> = {}) => {
  return useMutation({
    mutationFn: async (params) => {
      const { data } = await deleteRundownColumn(params)

      return data
    },
    onSuccess(data) {
      // TODO: Add invalidations if needed
      console.debug('DELETED COLUMN RETURN', data)
    },
    ...options,
  })
}

export const useClearRundownColumn = ({
  options,
}: MutationHook<RundownCell[], ClearRundownColumnParams> = {}) => {
  return useMutation({
    ...options,
    mutationFn: async (params) => {
      const { data } = await clearRundownColumn(params)

      return data
    },
    onSuccess(/* cells */) {},
  })
}

export const useClearRundownPrivateColumn = ({
  options,
}: MutationHook<RundownCell[], ClearRundownColumnParams> = {}) => {
  return useMutation({
    ...options,
    mutationKey: rundownMutationKeys.clearRundownPrivateColumn,
    mutationFn: async (params) => {
      const { data } = await clearRundownPrivateColumn(params)

      return data
    },
    onSuccess(/* cells */) {},
  })
}

export const useUploadRundownFile = ({
  options,
}: MutationHook<UploadRundownFileDto, UploadRundownFileParams> = {}) => {
  return useMutation({
    ...options,
    mutationFn: async (params) => {
      const { data } = await uploadRundownFile(params)

      return data
    },
  })
}

export const useAddRundownCue = ({
  options,
}: MutationHook<AddRundownCueDto, AddRundownCueParams> = {}) => {
  const { setQueryData } = useQueryClient()

  const setCues = useSetAtom(setCuesAtom)
  const setCueOrder = useSetAtom(setCueOrderAtom)

  return useMutation({
    ...options,
    mutationKey: rundownMutationKeys.addCue,
    mutationFn: async (params) => {
      const { data } = await addRundownCue(params)

      return data
    },
    onSuccess([rundown, cues]) {
      setQueryData(
        rundownQueryKeys.byId({
          rundownId: rundown.id,
        }).queryKey,
        rundown,
      )
      setCueOrder(rundown.cues)
      setCues(cues)
    },
  })
}

export const useUpdateRundownCue = ({
  options,
}: MutationHook<RundownCue, UpdateRundownCueParams> = {}) => {
  const setCue = useSetAtom(setCueAtom)

  return useMutation({
    ...options,
    mutationFn: async (params) => {
      const { data } = await updateRundownCue(params)

      return data
    },
    onSuccess(cue) {
      setCue(cue)
    },
  })
}

export const useDuplicateRundownCue = ({
  options,
}: MutationHook<DuplicateRundownCueDto, DuplicateRundownCueParams> = {}) => {
  const setCueOrder = useSetAtom(setCueOrderAtom)
  const setCues = useSetAtom(setCuesAtom)
  const setCells = useSetAtom(setCellsAtom)

  return useMutation({
    ...options,
    mutationFn: async (params) => {
      const { data } = await duplicateRundownCue(params)

      return data
    },
    onSuccess({ rundown, cues, cells }) {
      setCueOrder(rundown.cues)
      setCues(cues)
      setCells(cells)
    },
  })
}

export const useDuplicateRundownCues = ({
  options,
}: MutationHook<DuplicateRundownCuesDto, DuplicateRundownCuesParams> = {}) => {
  return useMutation({
    ...options,
    mutationFn: async (params) => {
      const { data } = await duplicateRundownCues(params)

      return data
    },
  })
}

export const useDeleteRundownCue = ({
  options,
}: MutationHook<Runner, DeleteRundownCueParams> = {}) => {
  const addToast = useSetAtom(toastAtom)

  return useMutation({
    ...options,
    mutationFn: async (params) => {
      const { data } = await deleteRundownCue(params)

      return data
    },
    onSuccess() {
      addToast({ title: `Cue moved to trash`, type: 'success' })
    },
  })
}

export const useCreateRundownRunner = ({
  options,
}: MutationHook<Rundown, CreateRundownRunnerParams> = {}) => {
  return useMutation({
    ...options,
    mutationFn: async (params) => {
      const { data } = await createRundownRunner(params)

      return data
    },
  })
}

export const useReorderRundownCues = ({
  options,
}: MutationHook<Rundown, ReorderRundownCuesParams> = {}) => {
  return useMutation({
    ...options,
    mutationFn: async (params) => {
      const { data } = await reorderRundownCues(params)

      return data
    },
  })
}

export const useGroupRundownCues = ({
  options,
}: MutationHook<Rundown, GroupRundownCuesParams> = {}) => {
  return useMutation({
    ...options,
    mutationFn: async (params) => {
      const { data } = await groupRundownCues(params)

      return data
    },
  })
}

export const useUngroupRundownCues = ({
  options,
}: MutationHook<Rundown, UngroupRundownCuesParams> = {}) => {
  return useMutation({
    ...options,
    mutationFn: async (params) => {
      const { data } = await ungroupRundownCues(params)

      return data
    },
  })
}

type CellHistoryParams = {
  rundownId: string
  cellId: string
}

type CellHistoryResponse = {
  history: Cell[]
  users: {
    [uid: string]: {
      displayName: string | undefined
    }
  }
}

export const useCellHistory = ({
  params,
  options,
}: QueryHook<CellHistoryResponse, CellHistoryParams>) => {
  return useQuery({
    ...options,
    queryKey: rundownQueryKeys.cellHistory(params.rundownId, params.cellId)
      .queryKey,
    queryFn: async () => {
      const { data } = await getCellHistory(params.rundownId, params.cellId)

      return data
    },
  })
}

type UpdateRundownCellParams = {
  rundownId: string
  cellId: string
  historyId: string
}

export const useUpdateRundownCell = ({
  options,
}: MutationHook<Cell, UpdateRundownCellParams> = {}) => {
  return useMutation({
    ...options,
    mutationFn: async (params) => {
      const { data } = await updateRundownCell(
        params.rundownId,
        params.cellId,
        params.historyId,
      )

      return data
    },
  })
}

export const useTemplatesByTeam = ({
  params,
  options,
}: QueryHook<Rundown[], GetRundownsByTeamParams>) => {
  return useQuery({
    ...options,
    queryKey: rundownQueryKeys.templatesByTeamId(params).queryKey,
    queryFn: async () => {
      const { data } = await getTemplatesByTeam(params)

      return data
    },
  })
}

export const useCloneRundown = ({
  options,
}: MutationHook<Rundown, CloneRundownParams> = {}) => {
  return useMutation({
    ...options,
    mutationFn: async (params) => {
      const { data } = await cloneRundown(params)

      return data
    },
  })
}
